import { useState, useEffect } from "react";
import api from "../utils/api/axiosInstance";
import { useUserAuth } from "../contexts/UserAuthContext";

const proofCache = new Map();
const CACHE_MS = 5 * 60 * 1000;

function cacheKey(productId, userId) {
  return `${productId}_${userId}`;
}

/**
 * Cluster social proof for a product (logged-in only).
 */
export function useProductSocialProof(productId, enabled = true) {
  const { user } = useUserAuth();
  const userId = user?.id || user?._id || null;
  const [proof, setProof] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !productId || !userId) {
      setProof(null);
      return undefined;
    }

    const key = cacheKey(productId, userId);
    const cached = proofCache.get(key);
    if (cached && Date.now() - cached.at < CACHE_MS) {
      setProof(cached.data);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    api
      .get(`/products/${productId}/social-proof`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data || null;
        proofCache.set(key, { data, at: Date.now() });
        setProof(data);
      })
      .catch(() => {
        if (!cancelled) setProof(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [productId, userId, enabled]);

  return { proof, loading, hasProof: Boolean(proof?.hasSocialProof) };
}
